import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Beaker,
  Brain,
  Check,
  Cpu,
  Loader2,
  Microscope,
  Shield,
  Zap,
} from 'lucide-react';
import Modal from '../../shared/Modal';
import ContactForm from '../../forms/ContactForm';
import {
  BLOOD_EVALUATION_DEPOSIT_USD,
  BLOOD_EVALUATION_INCLUDES,
  BLOOD_FULL_EVALUATION_USD,
  BLOOD_ORGANIZATION_TYPES,
  BLOOD_PATENT,
  type BloodEvaluationIntake,
  type BloodOrganizationType,
} from '../../../lib/bloodPartnership';
import { startBloodEvaluationCheckout } from '../../../lib/bloodCheckout';

const PATENT = BLOOD_PATENT;

const HIGHLIGHTS = [
  {
    icon: Brain,
    title: 'Bio-inspired architecture',
    text: 'A vein-like network within computing platforms — enhanced memory, communication, and processing beyond traditional architectures.',
  },
  {
    icon: Zap,
    title: 'Computational power',
    text: 'Proprietary integration techniques for massive parallel processing and adaptability on complex problem-solving workloads.',
  },
  {
    icon: Cpu,
    title: 'Advanced neural networks',
    text: 'Enhanced connectivity, plasticity, and synchronization for AI systems with stronger learning and decision-making.',
  },
  {
    icon: Beaker,
    title: 'Multidisciplinary R&D',
    text: 'Bio-nanotechnology, engineered exosomes, and optogenetics — bridging biology, nanotech, AI, and computational neuroscience.',
  },
];

const APPLICATIONS = [
  {
    icon: Shield,
    title: 'Advanced computing',
    text: 'Systems built for cryptography, complex simulations, and large-scale data processing beyond conventional paradigms.',
  },
  {
    icon: Cpu,
    title: 'Next-generation AI',
    text: 'More adaptive autonomous systems, predictive analytics, and machine learning across industries and robotics.',
  },
  {
    icon: Microscope,
    title: 'Research platforms',
    text: 'A foundation for computational neuroscience, brain-computer interfaces, and next-gen computing architecture research.',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

export default function BloodPage() {
  const [searchParams] = useSearchParams();
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [intake, setIntake] = useState<BloodEvaluationIntake>({
    contactName: '',
    email: '',
    company: '',
    jobTitle: '',
    organizationType: 'Humanoid robotics OEM',
    useCase: '',
  });

  const updateIntake = <K extends keyof BloodEvaluationIntake>(
    key: K,
    value: BloodEvaluationIntake[K]
  ) => {
    setIntake((prev) => ({ ...prev, [key]: value }));
    setCheckoutError(null);
  };

  const handleEvaluationDeposit = async () => {
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      await startBloodEvaluationCheckout(intake);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Checkout failed');
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>BLOOD — SELF Labs</title>
        <meta
          name="description"
          content="BLOOD is SELF Labs bio-nanotechnology for advanced computing — engineered exosomes, optogenetics, and patent-pending architecture for AI and humanoid robotics."
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/products"
                className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to Products
              </Link>
            </motion.div>

            {checkoutCancelled && (
              <p className="mt-6 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
                Checkout was cancelled. Complete the form below when you&apos;re ready.
              </p>
            )}

            {/* Hero */}
            <section className="mx-auto max-w-3xl pb-16 pt-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                  <Microscope className="h-3.5 w-3.5" />
                  Patent pending
                </span>
                <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">BLOOD</h1>
                <p className="mt-2 text-sm font-medium text-white/45">
                  Biological and Lightweight Organics Optimized for Dynamic memory systems
                </p>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                  Bio-nanotechnology integrating engineered exosomes and optogenetics for advanced
                  computing platforms and humanoid robots — a breakthrough architecture from SELF Labs.
                </p>
                <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
                  Protected under {PATENT}. BLOOD transcends traditional computing paradigms at the
                  intersection of biology, AI, and neural systems.
                </p>
              </motion.div>
            </section>

            {/* Overview */}
            <section className="border-t border-white/10 py-16">
              <div className="mx-auto max-w-3xl">
                <motion.div {...fadeUp} className="rounded-2xl border border-white/12 bg-white/[0.03] p-8 sm:p-10">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Technology
                  </p>
                  <h2 className="mt-3 text-2xl font-bold sm:text-3xl">
                    Biology meets computing
                  </h2>
                  <p className="mt-6 leading-relaxed text-white/65">
                    BLOOD creates a sophisticated, bio-inspired network within computing platforms —
                    enabling enhanced memory storage, communication, and computational capabilities that
                    reimagine what advanced AI and neural networks can become.
                  </p>
                  <blockquote className="mt-8 border-l-2 border-white/20 pl-5 text-white/55 italic">
                    &ldquo;We&apos;ve created technology that doesn&apos;t just improve on existing paradigms —
                    it reimagines what&apos;s possible for advanced computing and AI systems.&rdquo;
                    <footer className="mt-3 text-sm not-italic text-white/40">
                      — Milan Cheeks, Founder of SELF Labs
                    </footer>
                  </blockquote>
                </motion.div>
              </div>
            </section>

            {/* Highlights */}
            <section className="border-t border-white/10 py-16">
              <div className="mx-auto max-w-4xl">
                <motion.div {...fadeUp} className="mb-10 text-center">
                  <h2 className="text-2xl font-bold">Core capabilities</h2>
                  <p className="mx-auto mt-3 max-w-xl text-white/55">
                    Transformative technology at the intersection of biology and computing.
                  </p>
                </motion.div>
                <div className="grid gap-6 sm:grid-cols-2">
                  {HIGHLIGHTS.map((item, i) => (
                    <motion.div
                      key={item.title}
                      {...fadeUp}
                      transition={{ delay: i * 0.06 }}
                      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                    >
                      <item.icon className="mb-4 h-7 w-7 text-white/70" strokeWidth={1.5} />
                      <h3 className="font-semibold">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* Applications */}
            <section className="border-t border-white/10 py-16">
              <div className="mx-auto max-w-3xl">
                <motion.div {...fadeUp} className="mb-10 text-center">
                  <h2 className="text-2xl font-bold">Applications</h2>
                  <p className="mx-auto mt-3 max-w-xl text-white/55">
                    Across advanced computing, AI, and research.
                  </p>
                </motion.div>
                <ul className="space-y-6">
                  {APPLICATIONS.map((item, i) => (
                    <motion.li
                      key={item.title}
                      {...fadeUp}
                      transition={{ delay: i * 0.06 }}
                      className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/12 bg-white/5">
                        <item.icon className="h-5 w-5 text-white/70" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{item.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Partnership */}
            <section className="border-t border-white/10 py-16">
              <div className="mx-auto max-w-3xl">
                <motion.div {...fadeUp} className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Partnerships
                  </p>
                  <h2 className="mt-3 text-2xl font-bold sm:text-3xl">
                    Start with a technology evaluation deposit
                  </h2>
                  <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/55">
                    BLOOD is offered through qualified licensing partnerships. Pay a{' '}
                    <strong className="font-medium text-white/75">
                      ${BLOOD_EVALUATION_DEPOSIT_USD.toLocaleString()} evaluation deposit
                    </strong>{' '}
                    to begin — credited toward the ${BLOOD_FULL_EVALUATION_USD.toLocaleString()}{' '}
                    full evaluation or a development license if you proceed.
                  </p>
                  <p className="mx-auto mt-2 text-xs text-white/40">{PATENT}</p>
                </motion.div>

                <motion.div
                  {...fadeUp}
                  className="mt-10 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8"
                >
                  <ul className="mb-8 space-y-2 text-sm text-white/60">
                    {BLOOD_EVALUATION_INCLUDES.map((line) => (
                      <li key={line} className="flex gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400/80" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm sm:col-span-2">
                      <span className="text-white/55">Your name</span>
                      <input
                        value={intake.contactName}
                        onChange={(e) => updateIntake('contactName', e.target.value)}
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="text-white/55">Email</span>
                      <input
                        type="email"
                        value={intake.email}
                        onChange={(e) => updateIntake('email', e.target.value)}
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="text-white/55">Job title</span>
                      <input
                        value={intake.jobTitle}
                        onChange={(e) => updateIntake('jobTitle', e.target.value)}
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                      />
                    </label>
                    <label className="block text-sm sm:col-span-2">
                      <span className="text-white/55">Company / organization</span>
                      <input
                        value={intake.company}
                        onChange={(e) => updateIntake('company', e.target.value)}
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                      />
                    </label>
                    <label className="block text-sm sm:col-span-2">
                      <span className="text-white/55">Organization type</span>
                      <select
                        value={intake.organizationType}
                        onChange={(e) =>
                          updateIntake(
                            'organizationType',
                            e.target.value as BloodOrganizationType
                          )
                        }
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black px-4 py-3 text-white [color-scheme:dark]"
                      >
                        {BLOOD_ORGANIZATION_TYPES.map((type) => (
                          <option key={type} value={type} className="bg-black text-white">
                            {type}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-sm sm:col-span-2">
                      <span className="text-white/55">
                        How are you evaluating BLOOD for humanoid robotics or advanced compute?
                      </span>
                      <textarea
                        value={intake.useCase}
                        onChange={(e) => updateIntake('useCase', e.target.value)}
                        rows={4}
                        className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3"
                        placeholder="Platform, integration goals, timeline, team size…"
                      />
                    </label>
                  </div>

                  {checkoutError && (
                    <p className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                      {checkoutError}
                    </p>
                  )}

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <button
                      type="button"
                      onClick={() => void handleEvaluationDeposit()}
                      disabled={checkoutLoading}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90 disabled:opacity-50 sm:flex-none"
                    >
                      {checkoutLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Opening checkout…
                        </>
                      ) : (
                        `Pay $${BLOOD_EVALUATION_DEPOSIT_USD.toLocaleString()} evaluation deposit`
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsContactModalOpen(true)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/35 hover:bg-white/10 sm:flex-none"
                    >
                      Ask a question first
                    </button>
                  </div>

                  <p className="mt-4 text-center text-xs text-white/40">
                    Secure Stripe checkout · A member of our team will follow up within 2 business
                    days after payment
                  </p>
                </motion.div>
              </div>
            </section>

            {/* Footer */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
                <p className="text-white/55">
                  Explore more SELF Labs research and products — from{' '}
                  <Link to="/cerebral-os" className="text-white/75 underline underline-offset-2 hover:text-white">
                    Cerebral OS
                  </Link>{' '}
                  to{' '}
                  <Link to="/aura-resonant-layer" className="text-white/75 underline underline-offset-2 hover:text-white">
                    AuRA Resonant Layer
                  </Link>
                  .
                </p>
              </motion.div>
            </section>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="BLOOD Technology Partnership Inquiry"
      >
        <ContactForm />
      </Modal>
    </>
  );
}
